/* eslint-disable @typescript-eslint/no-explicit-any */
// types/index.ts

import type { ArabicNormalizationOptions } from './lib/arabicNormalization'

// ==========================
// Discriminated Constants
// ==========================

export const LessonType = {
    VIDEO: 'VIDEO',
    AUDIO: 'AUDIO',
    QUIZ: 'QUIZ',
    PRACTICE: 'PRACTICE',
} as const
export type LessonType = (typeof LessonType)[keyof typeof LessonType]

/** Human-readable label for a lesson type — safe to display in any UI. */
export const lessonTypeLabel = (type: string): string => {
    switch (type.toUpperCase()) {
        case 'VIDEO':    return 'Video'
        case 'AUDIO':    return 'Audio'
        case 'QUIZ':     return 'Quiz'
        case 'PRACTICE': return 'Practice'
        default: return type.toLowerCase().replace(/_/g, ' ')
    }
}

/**
 * The subject a class belongs to — the catalog's top-level grouping.
 *
 * The axis CATALOG-STRATEGY.md locked on 2026-07-14: one subject = one class. It replaces
 * grouping by `targetAudience`, which is being retired. Nullable on a class: unfiled classes
 * group under their own heading rather than being guessed into a bucket.
 * Mirrors the backend enum `com.dhtn.model.ClassCategory`.
 */
export const ClassCategory = {
    QURAN: 'QURAN',
    ISLAMIC_SCIENCES: 'ISLAMIC_SCIENCES',
    TECHNOLOGY: 'TECHNOLOGY',
    DESIGN: 'DESIGN',
    LANGUAGE: 'LANGUAGE',
    BUSINESS: 'BUSINESS',
    SKILLS_ACQUISITION: 'SKILLS_ACQUISITION',
    PERSONAL_DEVELOPMENT: 'PERSONAL_DEVELOPMENT',
    HISTORY: 'HISTORY',
} as const
export type ClassCategory = (typeof ClassCategory)[keyof typeof ClassCategory]

/** Catalog display order. The page renders sections in this order, then unfiled classes last. */
export const CLASS_CATEGORY_ORDER: ClassCategory[] = [
    'QURAN',
    'ISLAMIC_SCIENCES',
    'TECHNOLOGY',
    'DESIGN',
    'LANGUAGE',
    'BUSINESS',
    'SKILLS_ACQUISITION',
    'PERSONAL_DEVELOPMENT',
    'HISTORY',
]

/**
 * ADMIN-ONLY labels, English by design — the admin UI is English-only (see CLAUDE.md).
 * Student-facing section names come from i18n (`classesPage.categories.*`), which is where
 * the Arabic and Hausa voices live. These two must not be confused.
 */
export const CLASS_CATEGORY_LABEL: Record<ClassCategory, string> = {
    QURAN: 'Qur’an',
    ISLAMIC_SCIENCES: 'Islamic knowledge',
    TECHNOLOGY: 'Technology',
    DESIGN: 'Design',
    LANGUAGE: 'Languages',
    BUSINESS: 'Business',
    SKILLS_ACQUISITION: 'Skills acquisition',
    PERSONAL_DEVELOPMENT: 'Personal development',
    HISTORY: 'History',
}

export const QuestionType = {
    SINGLE_CHOICE: 'SINGLE_CHOICE',
    MULTIPLE_CHOICE: 'MULTIPLE_CHOICE',
    TRUE_FALSE: 'TRUE_FALSE',
} as const
export type QuestionType = (typeof QuestionType)[keyof typeof QuestionType]

export const LanguageCode = {
    EN: 'en',
    AR: 'ar',
    HA: 'ha',
} as const
export type LanguageCode = (typeof LanguageCode)[keyof typeof LanguageCode]

// ==========================
// Language Configuration
// ==========================

export interface LanguageConfig {
    code: LanguageCode
    name: string
    label: string
    nativeName: string
    flag?: string
}

export const LANGUAGES: LanguageConfig[] = [
    { code: LanguageCode.EN, name: 'English', label: 'EN', nativeName: 'English' },
    { code: LanguageCode.AR, name: 'Arabic', label: 'AR', nativeName: 'العربية' },
    { code: LanguageCode.HA, name: 'Hausa', label: 'HA', nativeName: 'Hausa' },
] as const

// ==========================
// Course — content only
// (title + modules + lessons + quizzes)
// All metadata lives on SchoolClass
// ==========================

export interface QuizOption {
    id?: string
    text: string
}

export interface Question {
    id?: string
    text: string
    type: QuestionType
    options: QuizOption[]
    correctOptionIndices: number[]
    explanation?: string
}

export interface Quiz {
    id?: string
    title: string
    questions: Question[]
    timeLimit?: number
    passingScore?: number
}

export interface LessonResource {
    name: string
    url: string
    premium: boolean
}

export interface LessonPlayback {
    lessonId: string
    videoId: string
    playbackUrl: string
    expiresAt: number
}

/**
 * A practice exercise ("atom"), named by the faculty it trains:
 *   Ear    — LISTEN_CHOOSE, LISTEN_BUILD
 *   Eye    — READ_CHOOSE, DECODE
 *   Hand   — FILL_BLANK, SENTENCE_ORDER, MATCHING, JOIN_LETTERS, TRACE
 *   Tongue — RECITE_COMPARE   (+ DICTATION, legacy)
 *   Mind   — APPLY_RULE, EXPLAIN   (CODE_RUN comes with the lesson-player build)
 * All persist as JSON inside `lesson.practiceData` — no backend schema per atom.
 */
export type PracticeExerciseType =
    | 'FILL_BLANK' | 'MATCHING' | 'DICTATION' | 'SENTENCE_ORDER'
    | 'DECODE' | 'LISTEN_CHOOSE' | 'LISTEN_BUILD' | 'READ_CHOOSE'
    | 'JOIN_LETTERS' | 'TRACE' | 'RECITE_COMPARE' | 'APPLY_RULE' | 'EXPLAIN'
    | 'CODE_RUN' | 'LETTER_GRID'
    | 'STORY' | 'STEP_REVEAL'
    | 'TRANSLATE' | 'DIALOGUE' | 'LIVE_PREVIEW' | 'PREDICT_OUTPUT'
    // Universal
    | 'GLOSSARY' | 'SCENARIO' | 'RECAP' | 'ESTIMATE' | 'SPOT_SIGN' | 'FLASHCARD'
    | 'HOTSPOT' | 'BRANCH'
    // Languages
    | 'WORD_IMAGE' | 'TRANSFORM' | 'FIX_MISTAKE' | 'SPEAK' | 'LISTEN_DIALOGUE'
    // Computer Science
    | 'PARSONS' | 'TOGGLE_LOGIC' | 'FIX_BUG' | 'TERMINAL' | 'PICK_BETTER'
    | 'CONNECT_NODES' | 'AI_PROMPT' | 'CHECKPOINT'

export interface PracticeExercise {
    type: PracticeExerciseType
    question: string
    data: {
        answer?: string
        acceptedAnswers?: string[]
        audioUrl?: string
        hint?: string
        transcript?: string
        showTranscriptAfterSubmit?: boolean
        correctSentence?: string
        alternateAnswers?: string[]
        words?: string[]
        grading?: ArabicNormalizationOptions
        pairs?: Record<string, string>
        // DECODE / LISTEN_CHOOSE / READ_CHOOSE — pick the correct option
        glyph?: string
        options?: string[]
        correctIndex?: number
        // READ_CHOOSE — passage read before answering
        passage?: string
        // JOIN_LETTERS — isolated letters → the joined word
        letters?: string[]
        word?: string
        // RECITE_COMPARE — reference the learner recites against
        referenceText?: string
        referenceAudioUrl?: string
        // APPLY_RULE — tap the correct word(s) inside a live sentence
        sentence?: string
        correctWords?: string[]
        // EXPLAIN — say it in your own words, then reveal the model answer
        prompt?: string
        modelAnswer?: string
        // CODE_RUN — write code, run it in-browser, check against test cases
        language?: string
        starterCode?: string
        codeTests?: { input?: string; expectedOutput: string }[]
        // LETTER_GRID — the "Learn the letters" front door: a grid of tap-to-hear cards
        letterGrid?: { glyph: string; name?: string; audioUrl?: string }[]
        // STORY — a passage→image→passage concept flow, graded on a closing question.
        // `blocks` render in order (text or an image asset); the question reuses the
        // shared `options` + `correctIndex` above, so it grades exactly like READ_CHOOSE.
        blocks?: { text?: string; imageUrl?: string; caption?: string }[]
        // STEP_REVEAL — unfold a derivation one line at a time (calm study atom, no grade).
        steps?: string[]
        // TRANSLATE — build the target sentence from tiles (`words` bank), graded against
        // `correctSentence` (+ `alternateAnswers`); `prompt` holds the source sentence to translate.
        // Reuses the shared tile machinery (see usesTiles) — no new fields needed beyond those above.
        // DIALOGUE — a short conversation; the learner picks their line (reuses `options`/`correctIndex`).
        // `audioUrl` per line is optional — a small inline play control on that bubble.
        lines?: { who: 'them' | 'you'; text: string; audioUrl?: string }[]
        // DIALOGUE multi-turn — a real exchange: each turn is a "them" message + the learner's reply
        // options; the correct reply drops in as their bubble and the next turn arrives. When `turns`
        // is absent it falls back to the single-turn `lines`/`options` shape above.
        turns?: { them: string; options: string[]; correctIndex: number; why?: string; audioUrl?: string }[]
        // PREDICT_OUTPUT — read `code` and choose what it prints (reuses `options`/`correctIndex`, `language`).
        // LIVE_PREVIEW — write front-end code (reuses `starterCode`, `language`) and see it render; calm study atom.
        code?: string

        // ── New atoms (2026-07-16), all additive; each reuses one existing grading channel ──
        // WHY → modal: shown as a "Why?" secondary button after a graded answer, on any gradable atom.
        why?: string
        // GLOSSARY (cross-cutting): author marks words with a meaning; the learner taps the
        // dotted-underlined word → a message-bubble popover. Attach to ANY atom with prose.
        glossary?: { term: string; meaning: string }[]
        // WORD_IMAGE / PICK_BETTER — picture options, parallel to `options` (which stay the
        // graded ids). Each entry is a self-contained SVG data-URI (rendered via <img>, never innerHTML).
        imageOptions?: string[]
        // RECAP — a calm closing summary: the two or three things to keep.
        points?: string[]
        // ESTIMATE — commit to a guess, then reveal the real figure (calm; no objective grade).
        estimate?: { min: number; max: number; actual: number; unit?: string; explanation: string }
        // TERMINAL — a safe, scripted shell: type a command, see the authored output.
        commands?: { cmd: string; output: string }[]
        intro?: string
        // CHECKPOINT — a project rubric to self-check, then submit for review (calm study).
        checkpoint?: { title?: string; items: string[] }
        // TOGGLE_LOGIC — switch on exactly the correct conditions.
        conditions?: string[]
        correctIndices?: number[]
        // PARSONS — order jumbled code lines (`codeLines` is the correct order); FIX_BUG — tap
        // the buggy line (`buggyLine` = its index). Both reuse the self-report 'correct' channel.
        codeLines?: string[]
        buggyLine?: number
        // CONNECT_NODES — tap the parts in the order a request flows (`nodes` = correct order).
        nodes?: string[]
        // FIX_MISTAKE — after tapping the wrong word, reveal its correct form.
        correction?: string
        // FLASHCARD — a two-sided card: tap to flip front↔back, then self-rate (calm; no grade).
        front?: string
        back?: string
        // HOTSPOT — tap the right zone on an author image/mockup. `imageUrl` renders via <img>
        // (a data-URI SVG or a raster screenshot); `zones` are %-of-the-box rectangles overlaid on
        // it (hover shows `label`); the zone with `correct` is the answer. Reuses top-level `why`.
        hotspot?: {
            imageUrl: string
            zones: { x: number; y: number; w: number; h: number; label?: string; correct?: boolean }[]
            note?: string
        }
        // BRANCH — a decision tree the learner steers. `nodes` keyed by id; `start` is the first.
        // A node has `text` + `opts` ({label, go}); a terminal node has `end` ('good'|'bad') + `why`.
        branch?: {
            start: string
            nodes: Record<string, {
                text: string
                opts?: { label: string; go: string }[]
                end?: 'good' | 'bad'
                why?: string
            }>
        }
    }
}

export type LessonLevel = 'BEGINNER' | 'INTERMEDIATE' | 'ADVANCED'

export interface Lesson {
    id?: string
    title: string
    type: LessonType
    // Difficulty level, or undefined = unspecified (no badge). Powers future onboarding routing.
    level?: LessonLevel
    content: string
    videoUrl?: string
    audioUrl?: string
    videoId?: string
    durationSeconds?: number
    processingStatus?: string
    resourceUrl?: string
    videoResources?: LessonResource[]
    interactiveData?: string
    durationMinutes: number
    completed: boolean
    order: number
    quizzes?: Quiz[]
    practiceData?: string
    practiceExercise?: PracticeExercise
    duration?: number
    // Free-access model: true = needs full access (not the free first course, not this course's
    // first-lesson teaser). The backend strips a locked lesson's body; the UI shows a warm
    // "Continue" (never a padlock) and the lesson opens the payment door instead of playing.
    locked?: boolean
}

export interface Module {
    id?: string
    title: string
    description?: string
    order: number
    // DERIVED (read-only): entry level computed from this module's lessons. Not authored.
    level?: LessonLevel
    lessons: Lesson[]
}

// Course = pure content container
export interface Course {
    id?: string
    title: string
    durationMinutes: number
    duration?: number // alias for durationMinutes (frontend compat)
    // DERIVED (read-only): entry level computed from this course's lessons. Not authored.
    level?: LessonLevel
    modules: Module[]
    createdAt?: string
    // Release switch. Publishing the class no longer exposes every course inside it — a course
    // stays hidden until this is set. Admin-only: students never receive an unpublished course.
    isPublished?: boolean
    // Student-specific (set when fetched inside a class context)
    isEnrolled?: boolean
    progress?: number
}

// ==========================
// SchoolClass — the product
// What students browse, subscribe to, and learn from
// ==========================

export interface ClassInstructor {
    name: string
    role: string
    avatar?: string
}

export interface StageCourse {
    lessons: Lesson[]
    // MODULES ARE A TIER on the class page: course → module → lesson, all three drawn.
    // `/classes/{id}` returns them alongside the flat `lessons` array (backend, 2026-07-29),
    // so the public page can group its curriculum WITHOUT calling GET /courses/{id} —
    // that endpoint is authenticated and 401s for a signed-out visitor, which once blanked
    // the whole lesson list for exactly the person we are trying to convince.
    // The flat `lessons` array stays: it is what "how many, and which is next" reads.
    modules?: Module[]
    id: string
    title: string
    durationMinutes: number
    // DERIVED (read-only): entry level computed from this course's lessons. Not authored.
    level?: LessonLevel
    moduleCount: number
    lessonCount: number
    // Free-access model: true = the whole course is free (the class's first course, the free
    // onboarding arc). In every other course only the first lesson (teaser) is unlocked.
    isFree?: boolean
    // true = the course is not published yet. The server sends only its title and module titles
    // (no lessons), so the page shows it as a "coming soon" outline that never opens or counts.
    comingSoon?: boolean
}

export interface ClassStage {
    id: string
    stageNumber: number
    title: string // e.g. "المرحلة الأولى"
    description?: string
    isLocked: boolean // true if student hasn't unlocked yet
    stageProgress: number // 0–100
    courses: StageCourse[]
}

export interface SchoolClass {
    id: string
    title: string
    description?: string
    thumbnail?: string
    slug?: string
    targetAudience?: string // "Youth", "Adults", "Children" — admin sets freely
    // Subject grouping for the catalog. Undefined = not filed yet.
    category?: ClassCategory
    // Authored entry/starting level for the catalog + onboarding routing. Course/module levels
    // are derived from lessons; this one is set by admin.
    level?: LessonLevel
    language: LanguageCode | string
    price: number
    isPublished: boolean // false = "Coming Soon"
    isHidden?: boolean   // true = hidden from student-facing pages
    enrollmentCount: number
    createdAt?: string
    instructor?: ClassInstructor
    learningOutcomes: string[]
    prerequisites: string[]
    stages: ClassStage[]

    // Student-specific (null when not logged in)
    isEnrolled?: boolean
    progress?: number
    currentStage?: number
    subscriptionStatus?: 'ACTIVE' | 'EXPIRED' | 'CANCELLED'
    subscriptionExpiresAt?: string
}

// ==========================
// User
// ==========================

export interface User {
    reviewer?: boolean
    reviewClassIds?: string[]
    id: string
    name: string
    email: string
    role: 'student' | 'admin'
    firstName?: string
    lastName?: string
    enabled?: boolean
    createdAt?: string
    updatedAt?: string
    hasActiveSubscription?: boolean
    subscriptionTier?: 'FREE' | 'FULL_ACCESS'
    subscriptionExpiresAt?: string
    /** "MONTHLY" | "YEARLY" | undefined (lifetime/whitelisted/no payment history). Only populated on single-user admin detail fetches. */
    subscriptionBillingCycle?: string
    /** Derived access flag: true only when subscription is active AND not expired. Mirrors real premium access — use for gating UI. */
    isPremium?: boolean
    provider?: string
    picture?: string
    avatar?: string
    hasPassword?: boolean
    phoneNumber?: string
    whatsappOptIn?: boolean
    preferredLanguage?: string
}

export interface BackendUser {
    id?: number | string
    email: string
    firstName?: string
    lastName?: string
    role: string
    hasActiveSubscription?: boolean
    subscriptionExpiresAt?: string
    subscriptionTier?: 'FREE' | 'FULL_ACCESS'
    provider?: string
    enabled?: boolean
    createdAt?: string
    updatedAt?: string
    preferredLanguage?: string
}

// ==========================
// Progress & Analytics
// ==========================

export interface UserProgress {
    lessonId: string
    completed: boolean
    timeSpentMinutes: number
    attemptCount: number
    lastAccessed?: string
    firstCompleted?: string
}

export interface UserAnalytics {
    totalLessonsCompleted: number
    totalLearningSeconds: number
    daysActiveThisMonth: number
    weeklyActivity: Array<{
        day: string
        date: string
        isToday: boolean
        seconds: number
        lessonsCompleted: number
    }>
}

export interface MonthlyAnalytics {
    year: number
    month: number // 1-12
    daysActive: number
    lessonsCompleted: number
    learningSeconds: number
    days: Array<{
        date: string   // ISO "YYYY-MM-DD"
        seconds: number
        lessonsCompleted: number
    }>
}

// ==========================
// Payment & Enrollment Types
// ==========================

export interface PaymentError extends Error {
    name: 'PaymentRequiredError' | 'SubscriptionRequiredError' | 'NetworkError' | 'AuthError'
    classId?: string
    classTitle?: string
    price?: number
    transactionId?: string
    [key: string]: any
}

export interface MockPaymentResult {
    success: boolean
    transactionId: string
    timestamp: string
    classId: string
    paymentMethod: string
    amount: number
    backendResponse?: any
}

export interface SubscriptionStatus {
    hasActiveSubscription: boolean
    expiresAt?: string
    tier: string
}

export interface EnrollmentRequest {
    classId: string
    userId?: string
    paymentMethod?: string
}

export interface EnrollmentResponse {
    success: boolean
    enrollmentId?: string
    classId: string
    userId: string
    enrolledAt: string
    currentStage: number
    message?: string
    requiresPayment?: boolean
}

// ==========================
// Form Types — Admin: Create/Edit Course (content only)
// ==========================

export interface FormLesson {
    id?: string
    title: string
    type: LessonType
    level?: LessonLevel
    durationMinutes: number
    content: string
    videoUrl?: string
    audioUrl?: string
    videoId?: string
    durationSeconds?: number
    processingStatus?: string
    resourceUrl?: string
    videoResources?: LessonResource[]
    practiceData?: string
    order: number
    quizzes?: FormQuiz[]
}

export interface FormQuiz {
    id?: string
    title: string
    questions: FormQuestion[]
    timeLimit?: number
    passingScore?: number
}

export interface FormQuestion {
    id?: string
    text: string
    type: QuestionType
    options: QuizOption[]
    correctOptionIndices: number[]
    explanation?: string
}

export interface FormModule {
    id?: string
    title: string
    description?: string
    order: number
    lessons: FormLesson[]
}

// Course form — content only, no metadata
export interface FormCourse {
    title: string
    modules: FormModule[]
    // Omit to leave the course's release state untouched; the backend only changes it when this
    // is explicitly present.
    isPublished?: boolean
}

// ==========================
// Form Types — Admin: Create/Edit Class
// ==========================

export interface FormStage {
    id?: string
    stageNumber: number
    title: string
    description?: string
    courses: { id: string }[] // just IDs — link existing courses
}

export interface FormClass {
    title: string
    description?: string
    thumbnail?: string
    slug?: string
    targetAudience?: string
    level?: LessonLevel
    // Subject grouping for the catalog. Undefined = unfiled; those classes group under
    // "More classes" at the end of /classes rather than being guessed into a subject.
    category?: ClassCategory
    language: LanguageCode | string
    price: number
    isPublished?: boolean
    instructor?: ClassInstructor
    learningOutcomes: string[]
    prerequisites: string[]
    stages?: FormStage[]
}

// ==========================
// Normalizers
// ==========================

export function normalizeCourse(course: any): Course {
    return {
        ...course,
        durationMinutes: course.durationMinutes || course.duration || 0,
        duration: course.durationMinutes || course.duration || 0,
        // Left undefined when the payload omits it, never coerced to false — the admin form must
        // be able to send "no opinion" so a content edit does not unpublish a live course.
        isPublished: course.isPublished ?? undefined,
        isEnrolled: course.isEnrolled || false,
        progress: course.progress || 0,
        createdAt: course.createdAt || new Date().toISOString(),
        modules: (course.modules || []).map((module: any, index: number) => ({
            id: module.id || `module-${index}`,
            title: module.title || `Module ${index + 1}`,
            description: module.description || '',
            order: module.order !== undefined ? module.order : index,
            level: module.level || undefined,
            lessons: (module.lessons || []).map((lesson: any, lessonIndex: number) => ({
                id: lesson.id || `lesson-${index}-${lessonIndex}`,
                title: lesson.title || `Lesson ${lessonIndex + 1}`,
                type: lesson.type || 'VIDEO',
                level: lesson.level || undefined,
                content: lesson.content || '',
                videoUrl: lesson.videoUrl || '',
                audioUrl: lesson.audioUrl || '',
                videoId: lesson.videoId || undefined,
                durationSeconds: lesson.durationSeconds ?? undefined,
                processingStatus: lesson.processingStatus || undefined,
                resourceUrl: lesson.resourceUrl || lesson.resourceLink || '',
                interactiveData: lesson.interactiveData || lesson.practiceData || '',
                durationMinutes: lesson.durationMinutes || lesson.duration || 0,
                duration: lesson.durationMinutes || lesson.duration || 0,
                completed: lesson.completed || false,
                order: lesson.order !== undefined ? lesson.order : lessonIndex,
                quizzes: lesson.quizzes || [],
                videoResources: lesson.videoResources || [],
                practiceData: lesson.practiceData || lesson.interactiveData || '',
                practiceExercise: lesson.practiceData
                    ? parsePracticeData(lesson.practiceData)
                    : undefined,
                locked: lesson.locked ?? false,
            })),
        })),
    }
}

/** A lesson as it arrives inside `/classes/{id}` — the slim shape a curriculum row
 *  needs. Shared by the flat `lessons` array and by `modules[].lessons`, so the two
 *  can never drift into different objects for the same lesson. */
function normalizeStageLesson(l: any) {
    return {
        id: l.id,
        title: l.title || '',
        type: l.type || 'VIDEO',
        level: l.level || undefined,
        durationMinutes: l.durationMinutes || 0,
        completed: l.completed || false,
        locked: l.locked ?? false,
    }
}

export function normalizeClass(sc: any): SchoolClass {
    return {
        ...sc,
        language: (sc.language || 'en').toLowerCase(),
        price: sc.price || 0,
        isPublished: sc.isPublished ?? false,
        isHidden: sc.isHidden ?? false,
        enrollmentCount: sc.enrollmentCount || 0,
        learningOutcomes: sc.learningOutcomes || [],
        prerequisites: sc.prerequisites || [],
        stages: (sc.stages || []).map((stage: any) => ({
            id: stage.id,
            stageNumber: stage.stageNumber,
            title: stage.title || `Stage ${stage.stageNumber}`,
            description: stage.description || '',
            isLocked: stage.isLocked ?? true,
            stageProgress: stage.stageProgress || 0,
            courses: (stage.courses || []).map((c: any) => ({
                id: c.id,
                title: c.title,
                durationMinutes: c.durationMinutes || 0,
                level: c.level || undefined,
                moduleCount: c.moduleCount || 0,
                lessonCount: c.lessonCount || 0,
                isFree: c.isFree ?? false,
                comingSoon: c.comingSoon === true,
                lessons: (c.lessons || []).map(normalizeStageLesson),
                // Carried through so the class page can draw the module tier. Left
                // undefined when the payload omits it — an empty array would read as
                // "this course genuinely has no modules" and silently flatten the page.
                modules: Array.isArray(c.modules)
                    ? c.modules.map((m: any, i: number) => ({
                          id: m.id || `module-${i}`,
                          title: m.title || '',
                          description: m.description || '',
                          order: m.order !== undefined ? m.order : i,
                          lessons: (m.lessons || []).map(normalizeStageLesson),
                      }))
                    : undefined,
            })),
        })),
        isEnrolled: sc.isEnrolled || false,
        progress: sc.progress || 0,
        currentStage: sc.currentStage || 1,
    }
}

// ==========================
// Announcement
// ==========================

export interface Announcement {
    id: string
    subject: string
    body: string
    targetType: 'ALL' | 'CLASS' | 'WAITLIST'
    targetClassId?: string
    targetClassName?: string
    recipientCount: number
    sentBy: string
    sentAt: string
    templateType?: 'general' | 'important' | 'new_resource' | 'celebration'
    channels?: 'EMAIL' | 'WHATSAPP' | 'BOTH'
}

// ==========================
// App Banner
// ==========================

export interface AppBanner {
    id: string
    message: string
    ctaText?: string
    ctaUrl?: string
}

// ==========================
// App Release (Android APK)
// ==========================

export interface AppRelease {
    version: string
    downloadUrl: string
}

// ── Achievements ──────────────────────────────────────────────────────────────

export interface CertificateDTO {
    id: string
    certificateNumber: string
    classId: string
    classTitle: string
    classLanguage: string
    completedAt: string
    issuedAt: string
    studentName: string
    downloadCount: number
}

// ── Course Mastery Summary ────────────────────────────────────────────────────

export interface CourseMasterySummary {
    courseId: string
    classId: string
    stageId: string
    /** Average of each graded lesson's best score, 0–100. */
    masteryPercent: number
    /** Average accuracy across best attempts per lesson, 0–100. */
    accuracyPercent: number
    /** Number of graded lessons whose best attempt passed. */
    lessonsMastered: number
    /** Number of distinct graded lessons with at least one attempt. */
    totalGradedLessons: number
    /** False when the student has never attempted any graded lesson here. */
    hasAttempts: boolean
    /** Best mastery score from the session before the most recent one. Null on first completion. */
    priorBestPercent?: number | null
}

export function normalizeCourseMasterySummary(raw: any): CourseMasterySummary {
    return {
        courseId: raw.courseId ?? '',
        classId: raw.classId ?? '',
        stageId: raw.stageId ?? '',
        masteryPercent: raw.masteryPercent ?? 0,
        accuracyPercent: raw.accuracyPercent ?? 0,
        lessonsMastered: raw.lessonsMastered ?? 0,
        totalGradedLessons: raw.totalGradedLessons ?? 0,
        hasAttempts: raw.hasAttempts ?? false,
        priorBestPercent: raw.priorBestPercent ?? null,
    }
}

// ── Mastery Summary (stage or class scope) ──────────────────────────────────

/** Same shape as {@link CourseMasterySummary}, for a STAGE (every course in
 * it) or a whole CLASS (every stage in it). `stageId` is null for a
 * CLASS-scope summary. */
export interface MasterySummary {
    classId: string
    stageId?: string | null
    masteryPercent: number
    accuracyPercent: number
    lessonsMastered: number
    totalGradedLessons: number
    hasAttempts: boolean
    priorBestPercent?: number | null
}

export function normalizeMasterySummary(raw: unknown): MasterySummary {
    const r = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>
    const num = (v: unknown): number => (typeof v === 'number' ? v : 0)
    return {
        classId: typeof r.classId === 'string' ? r.classId : '',
        stageId: typeof r.stageId === 'string' ? r.stageId : null,
        masteryPercent: num(r.masteryPercent),
        accuracyPercent: num(r.accuracyPercent),
        lessonsMastered: num(r.lessonsMastered),
        totalGradedLessons: num(r.totalGradedLessons),
        hasAttempts: r.hasAttempts === true,
        priorBestPercent: typeof r.priorBestPercent === 'number' ? r.priorBestPercent : null,
    }
}

// ── Admin Whitelist ───────────────────────────────────────────────────────────

export interface WhitelistedEmailEntry {
    reviewClassIds?: string[]
    id: string
    email: string
    note?: string
    createdAt: string
}

function parsePracticeData(practiceData: string): PracticeExercise | undefined {
    try {
        const parsed = JSON.parse(practiceData)
        if (parsed.type === 'FILL_BLANK') {
            return {
                type: 'FILL_BLANK',
                question: parsed.question || '',
                data: { answer: parsed.answer || '', hint: parsed.hint || '' },
            }
        } else if (parsed.type === 'MATCHING') {
            return {
                type: 'MATCHING',
                question: parsed.question || 'Match the following:',
                data: { pairs: parsed.pairs || {} },
            }
        } else if (parsed.type === 'DICTATION') {
            return {
                type: 'DICTATION',
                question: parsed.question || '',
                data: {
                    audioUrl: parsed.data?.audioUrl || parsed.audioUrl || '',
                    acceptedAnswers: parsed.data?.acceptedAnswers || parsed.acceptedAnswers || [],
                    hint: parsed.data?.hint || parsed.hint || '',
                    transcript: parsed.data?.transcript || parsed.transcript || '',
                    showTranscriptAfterSubmit: parsed.data?.showTranscriptAfterSubmit ?? parsed.showTranscriptAfterSubmit ?? true,
                    grading: parsed.data?.grading || parsed.grading || {},
                },
            }
        } else if (parsed.type === 'SENTENCE_ORDER') {
            return {
                type: 'SENTENCE_ORDER',
                question: parsed.question || '',
                data: {
                    correctSentence: parsed.data?.correctSentence || parsed.correctSentence || '',
                    alternateAnswers: parsed.data?.alternateAnswers || parsed.alternateAnswers || [],
                    words: parsed.data?.words || parsed.words || [],
                    hint: parsed.data?.hint || parsed.hint || '',
                    grading: parsed.data?.grading || parsed.grading || {},
                },
            }
        }
        return undefined
    } catch {
        return undefined
    }
}
